import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../supabaseClient/supabaseClient";
import "./resetPassword.css";

const PasswordReset = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isValidSession, setIsValidSession] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if we have a valid recovery session
    const checkSession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("Session error:", error);
          setMessage({
            text: "Invalid or expired reset link. Please request a new password reset.",
            type: "error",
          });
          setSessionLoading(false);
          return;
        }

        // Check if we have the necessary URL fragments for password recovery
        const accessToken = searchParams.get("access_token");
        const refreshToken = searchParams.get("refresh_token");
        const type = searchParams.get("type");

        if (type === "recovery" && accessToken && refreshToken) {
          // Set the session with the tokens from URL
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (setSessionError) {
            console.error("Set session error:", setSessionError);
            setMessage({
              text: "Invalid or expired reset link. Please request a new password reset.",
              type: "error",
            });
          } else {
            setIsValidSession(true);
          }
        } else if (session?.user) {
          // User is already logged in, check if they came from a recovery flow
          setIsValidSession(true);
        } else {
          setMessage({
            text: "Invalid or expired reset link. Please request a new password reset.",
            type: "error",
          });
        }
      } catch (error) {
        console.error("Session check error:", error);
        setMessage({
          text: "Something went wrong. Please try again.",
          type: "error",
        });
      } finally {
        setSessionLoading(false);
      }
    };

    checkSession();
  }, [searchParams]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // Clear any existing messages when user starts typing
    if (message.text) {
      setMessage({ text: "", type: "" });
    }
  };

  const validatePasswords = () => {
    if (formData.password.length < 6) {
      setMessage({
        text: "Password must be at least 6 characters long",
        type: "error",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage({ text: "Passwords do not match", type: "error" });
      return false;
    }

    return true;
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (error) {
        throw error;
      }

      setMessage({
        text: "Password updated successfully! Redirecting...",
        type: "success",
      });

      // Clear the form
      setFormData({ password: "", confirmPassword: "" });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    } catch (error) {
      console.error("Password update error:", error);
      setMessage({
        text: error.message || "Failed to update password. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login", { replace: true });
  };

  if (sessionLoading) {
    return (
      <div className="password-reset-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-card">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Invalid Reset Link</h2>
            <p>This password reset link is invalid or has expired.</p>
            {message.text && (
              <div className={`message ${message.type}`}>{message.text}</div>
            )}
            <button onClick={handleBackToLogin} className="btn btn-primary">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        {/* Header */}
        <div className="reset-header">
          <div className="reset-icon">🔐</div>
          <h2>Set New Password</h2>
          <p>Please enter your new password below</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        {/* Password Reset Form */}
        <form onSubmit={handlePasswordUpdate} className="password-reset-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                placeholder="Create a password (min. 6 characters)"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "View"}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              placeholder="Confirm your new password"
              disabled={loading}
            />
                           <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? "Hide" : "View"}
                </button>
          </div>
          </div>
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? "Updating Password..." : "Update Password"}
          </button>
        </form>

        {/* Footer */}
        <div className="reset-footer">
          <button onClick={handleBackToLogin} className="link-button">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
